import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDataContext } from "@/context/DataContext";

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: "privacy" | "terms";
  onAccept?: () => void;
}

export default function LegalModal({
  isOpen,
  onClose,
  initialTab = "privacy",
  onAccept,
}: LegalModalProps) {
  const { t, i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const [activeTab, setActiveTab] = useState<"privacy" | "terms">(initialTab);
  const { clinicInfo } = useDataContext();

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  const rawPrivacySections = clinicInfo?.privacySections || [];
  const rawTermsSections = clinicInfo?.termsSections || [];

  const privacySections = rawPrivacySections.map((s: any) => ({
    title: isAr ? (s.title || s.title_en || "") : (s.title_en || s.title || ""),
    content: isAr ? (s.content || s.content_en || "") : (s.content_en || s.content || ""),
  }));

  const termsSections = rawTermsSections.map((s: any) => ({
    title: isAr ? (s.title || s.title_en || "") : (s.title_en || s.title || ""),
    content: isAr ? (s.content || s.content_en || "") : (s.content_en || s.content || ""),
  }));

  const sections = activeTab === "privacy" ? privacySections : termsSections;
  const currentSummary = activeTab === "privacy"
    ? (isAr ? clinicInfo?.privacy : (clinicInfo?.privacyEn || clinicInfo?.privacy))
    : (isAr ? clinicInfo?.terms : (clinicInfo?.termsEn || clinicInfo?.terms));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir={isAr ? "rtl" : "ltr"}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      ></div>

      {/* Modal Box */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden animate-scale-in text-start border border-brand-cream-200">
        {/* Header */}
        <div className="bg-brand-forest-800 text-white px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <i className={activeTab === "privacy" ? "ri-shield-check-line text-xl" : "ri-file-text-line text-xl"}></i>
            </div>
            <div>
              <h3 className="font-bold text-base text-brand-cream-100">
                {activeTab === "privacy"
                  ? (isAr ? "سياسة الخصوصية وحماية البيانات" : "Privacy & Data Protection Policy")
                  : (isAr ? "شروط وأحكام الاستخدام والخدمة" : "Terms & Conditions of Service")}
              </h3>
              <p className="text-xs text-brand-cream-300/80">
                {isAr ? "منصة ذا مديكال أفينيو للطب الدقيق" : "The Medical Avenue Healthcare Platform"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title={isAr ? "إغلاق" : "Close"}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-brand-cream-200 bg-brand-cream-50/80 px-6 pt-3 gap-2 flex-shrink-0">
          <button
            onClick={() => setActiveTab("privacy")}
            className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === "privacy"
                ? "border-brand-forest-600 text-brand-forest-700 font-black"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-shield-keyhole-line"></i>
            {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
          </button>
          <button
            onClick={() => setActiveTab("terms")}
            className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 cursor-pointer flex items-center gap-1.5 ${
              activeTab === "terms"
                ? "border-brand-forest-600 text-brand-forest-700 font-black"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="ri-file-list-3-line"></i>
            {isAr ? "شروط الاستخدام" : "Terms of Use"}
          </button>
          
          <a
            href={`/legal?tab=${activeTab}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ms-auto pb-3 text-xs text-brand-forest-600 hover:text-brand-forest-800 font-semibold flex items-center gap-1 cursor-pointer"
          >
            <span>{isAr ? "فتح في صفحة كاملة" : "Open full page"}</span>
            <i className="ri-external-link-line text-[11px]"></i>
          </a>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 divide-y divide-brand-cream-100">
          {currentSummary && (
            <div className="bg-brand-cream-50 rounded-2xl p-4 border border-brand-cream-200/80">
              <p className="text-xs font-bold text-brand-forest-700 mb-1">
                {isAr ? "ملخص معتمد:" : "Approved Summary:"}
              </p>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed font-medium">
                {currentSummary}
              </p>
            </div>
          )}

          <div className="space-y-4 pt-4 first:pt-0">
            {sections.map((section, idx) => {
              const cleanTitle = (section.title || "").replace(/^\d+[\.\-\:]\s*/, "");
              return (
                <div key={idx} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:border-brand-cream-300 transition-colors">
                  <h4 className="font-bold text-sm text-brand-forest-900 mb-1.5 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-brand-forest-100 text-brand-forest-700 text-[11px] font-black flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    {cleanTitle || section.title}
                  </h4>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed ps-7 whitespace-pre-wrap">
                    {section.content}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-brand-cream-50 px-6 py-4 border-t border-brand-cream-200 flex items-center justify-between gap-3 flex-shrink-0">
          <span className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <i className="ri-lock-2-line text-brand-forest-600"></i>
            {isAr ? "جميع البيانات محمية بنظام تشفير معتمد" : "All data is securely encrypted"}
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-200/60 transition-colors cursor-pointer"
            >
              {isAr ? "إغلاق" : "Close"}
            </button>
            {onAccept && (
              <button
                onClick={() => {
                  onAccept();
                  onClose();
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-brand-forest-600 hover:bg-brand-forest-700 text-white transition-colors cursor-pointer shadow-sm flex items-center gap-1.5"
              >
                <i className="ri-check-line"></i>
                {isAr ? "أوافق وأفهم" : "I Agree & Understand"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
